const ATTRIBUTION_STORAGE_KEY = "callsect:umami-attribution-v1";
const ATTRIBUTION_TTL_MS = 30 * 24 * 60 * 60 * 1000;
const UTM_KEYS = ["utm_source", "utm_medium", "utm_campaign", "utm_content", "utm_term"];

const INTERACTION_STORAGE_KEY = "callsect:umami-interaction-confirmed-v1";

function hasConfirmedInteraction() {
  if (typeof window === "undefined") return false;
  try {
    return window.sessionStorage.getItem(INTERACTION_STORAGE_KEY) === "1";
  } catch {
    return false;
  }
}

export function ensureUmamiInteractionSignal() {
  if (typeof window === "undefined" || !hasConfirmedInteraction()) return;
  try {
    window.umami?.identify?.({ interaction_confirmed: true });
  } catch {
    // Analytics must never interrupt the public or product workflow.
  }
}

export function markUmamiInteractionConfirmed() {
  if (typeof window === "undefined") return;
  try {
    window.sessionStorage.setItem(INTERACTION_STORAGE_KEY, "1");
  } catch {
    // A blocked sessionStorage should not affect the app.
  }
  ensureUmamiInteractionSignal();
}

export function trackUmamiPageview() {
  if (typeof window === "undefined") return false;
  try {
    if (typeof window.umami?.track !== "function") return false;
    window.umami.track();
    return true;
  } catch {
    // Analytics must never interrupt the public or product workflow.
    return false;
  }
}

function cleanString(value, maxLength = 500) {
  if (typeof value !== "string") return undefined;
  const normalized = value.trim();
  return normalized ? normalized.slice(0, maxLength) : undefined;
}

function cleanEventData(data = {}) {
  return Object.fromEntries(
    Object.entries(data)
      .map(([key, value]) => {
        if (typeof value === "string") return [key, cleanString(value)];
        if (typeof value === "number" && Number.isFinite(value)) return [key, value];
        if (typeof value === "boolean") return [key, value];
        return [key, undefined];
      })
      .filter(([, value]) => value !== undefined)
  );
}

function readStoredAttribution() {
  if (typeof window === "undefined") return {};

  try {
    const raw = window.localStorage.getItem(ATTRIBUTION_STORAGE_KEY);
    if (!raw) return {};

    const stored = JSON.parse(raw);
    if (!stored?.expires_at || Date.now() > stored.expires_at) {
      window.localStorage.removeItem(ATTRIBUTION_STORAGE_KEY);
      return {};
    }

    return cleanEventData(stored.data || {});
  } catch {
    return {};
  }
}

export function captureUmamiAttribution() {
  if (typeof window === "undefined") return {};

  try {
    const params = new URLSearchParams(window.location.search);
    const campaign = Object.fromEntries(
      UTM_KEYS
        .map((key) => [key, cleanString(params.get(key) || "", 200)])
        .filter(([, value]) => value)
    );

    if (!Object.keys(campaign).length) return readStoredAttribution();

    const data = cleanEventData({
      ...campaign,
      landing_page: window.location.pathname || "/",
    });

    window.localStorage.setItem(
      ATTRIBUTION_STORAGE_KEY,
      JSON.stringify({
        expires_at: Date.now() + ATTRIBUTION_TTL_MS,
        data,
      })
    );

    return data;
  } catch {
    return {};
  }
}

export function trackUmamiEvent(eventName, data = {}) {
  if (typeof window === "undefined" || !eventName) return;

  const attribution = captureUmamiAttribution();
  const eventData = cleanEventData({
    ...attribution,
    ...data,
  });

  try {
    window.umami?.track?.(eventName, eventData);
  } catch {
    // Analytics must never interrupt the public or product workflow.
  }
}
